import { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { useTourGuideHover } from "./useTourGuideHover";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { ROUTE_NAMES } from "@root/constants";

export const useLegalChatbotTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const {
    setHighlightedItemKey,
    setActiveTourModule,
    setCurrentTourStep,
    hoverTooltipsEnabled,
  } = useTourGuideContext();

  // Tour guide refs
  const modelSelectorRef = useRef<HTMLDivElement>(null);
  const pillToggleRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLDivElement>(null);
  const suggestedQuestionsRef = useRef<HTMLDivElement>(null);

  const isLegalChatbot = pathname === ROUTE_NAMES.LEGAL_CHATBOT;

  // Tour guide configuration for legal chatbot
  const legalChatbotTourConfig: TourGuideConfig = useMemo(() => {
    if (!isLegalChatbot) {
      return {
        moduleId: "legalChatbot",
        steps: [],
        enabled: false,
      };
    }

    return {
      moduleId: "legalChatbot",
      enabled: true,
      steps: [
        {
          id: "sidebar-legal",
          targetSelector: '[data-tour-item="legalChatbot"]',
          title:
            t("tourGuide.legalChatbot.step1.title") ||
            "Welcome to Legal AI Agent! 👋",
          description:
            t("tourGuide.legalChatbot.step1.description") ||
            "Let's take a quick tour to help you understand how to use this powerful legal research assistant. This navigation menu allows you to access different sections.",
          position: "right" as const,
          offset: { x: 20, y: 0 },
        },
        {
          id: "model-selector",
          targetRef: modelSelectorRef,
          title:
            t("tourGuide.legalChatbot.step2.title") || "AI Model Selection",
          description:
            t("tourGuide.legalChatbot.step2.description") ||
            "Choose between different AI models (Claude Sonnet 3.7 or MIDM 2.0) based on your needs. Each model has different strengths for legal research.",
          position: "right" as const,
          offset: { x: 20, y: 0 },
        },
        {
          id: "pill-toggle",
          targetRef: pillToggleRef,
          title:
            t("tourGuide.legalChatbot.step3.title") || "Search Mode Selection",
          description:
            t("tourGuide.legalChatbot.step3.description") ||
            "Switch between Web Search and Law Info Center modes. Web Search finds information from the internet, while Law Info Center uses your uploaded legal documents.",
          position: "bottom" as const,
          offset: { x: 0, y: 12 },
        },
        {
          id: "textarea",
          targetRef: textareaRef,
          title:
            t("tourGuide.legalChatbot.step4.title") || "Ask Your Questions",
          description:
            t("tourGuide.legalChatbot.step4.description") ||
            "Type your legal questions here. You can ask about construction laws, regulations, court rulings, and legal precedents. Be specific for better results!",
          position: "top" as const,
          offset: { x: 0, y: -12 },
        },
        {
          id: "chatbot-dropdown",
          targetSelector: '[data-tour-item="chatbot-dropdown"]',
          title:
            t("tourGuide.legalChatbot.step5.title") || "Additional Actions",
          description:
            t("tourGuide.legalChatbot.step5.description") ||
            "Click the plus icon to access additional options like uploading documents, selecting files, or switching between different chatbot modes.",
          position: "top" as const,
          offset: { x: 0, y: -12 },
        },
        {
          id: "suggested-questions",
          targetRef: suggestedQuestionsRef,
          title:
            t("tourGuide.legalChatbot.step6.title") || "Suggested Questions",
          description:
            t("tourGuide.legalChatbot.step6.description") ||
            "Browse suggested questions and FAQs to get started quickly. Click on any question to use it as your query.",
          position: "top" as const,
          offset: { x: 0, y: -12 },
        },
      ],
    };
  }, [isLegalChatbot, t]);

  const baseTourGuide = useTourGuide(legalChatbotTourConfig);

  // Auto-start tour guide and handle completion tracking
  const legalChatbotTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourCompletionFlag: "legal_standard_tour_completed",
    isOnTourPath: isLegalChatbot,
  });

  // Sync tour guide with context
  useEffect(() => {
    if (legalChatbotTourGuide.isActive) {
      setActiveTourModule("legalChatbot");
      setCurrentTourStep(legalChatbotTourGuide.currentStep);
      if (legalChatbotTourGuide.currentStep === 0) {
        setHighlightedItemKey("legalChatbot");
      } else {
        setHighlightedItemKey(null);
      }
    } else {
      setActiveTourModule(null);
      setCurrentTourStep(0);
      setHighlightedItemKey(null);
    }
  }, [
    legalChatbotTourGuide.isActive,
    legalChatbotTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
    setHighlightedItemKey,
  ]);

  // Hover tooltips for all steps
  const modelSelectorStep = legalChatbotTourConfig.steps.find(
    (step) => step.id === "model-selector"
  );
  const pillToggleStep = legalChatbotTourConfig.steps.find(
    (step) => step.id === "pill-toggle"
  );
  const textareaStep = legalChatbotTourConfig.steps.find(
    (step) => step.id === "textarea"
  );
  const chatbotDropdownStep = legalChatbotTourConfig.steps.find(
    (step) => step.id === "chatbot-dropdown"
  );
  const suggestedQuestionsStep = legalChatbotTourConfig.steps.find(
    (step) => step.id === "suggested-questions"
  );

  const modelSelectorHover = useTourGuideHover(
    "model-selector",
    modelSelectorStep || null
  );
  const pillToggleHover = useTourGuideHover(
    "pill-toggle",
    pillToggleStep || null
  );
  const textareaHover = useTourGuideHover("textarea", textareaStep || null);
  const chatbotDropdownHover = useTourGuideHover(
    "chatbot-dropdown",
    chatbotDropdownStep || null
  );
  const suggestedQuestionsHover = useTourGuideHover(
    "suggested-questions",
    suggestedQuestionsStep || null
  );

  // Attach hover handlers to chatbot dropdown via selector (it's in dashboardtextarea)
  useEffect(() => {
    if (
      !hoverTooltipsEnabled ||
      legalChatbotTourGuide.isActive ||
      !chatbotDropdownStep
    )
      return;

    const element = document.querySelector(
      '[data-tour-item="chatbot-dropdown"]'
    ) as HTMLElement;
    if (element) {
      const handleMouseEnter = () => {
        if (!chatbotDropdownHover.tooltipData) return;
        chatbotDropdownHover.hoverProps.onMouseEnter();
      };
      const handleMouseLeave = () => {
        chatbotDropdownHover.hoverProps.onMouseLeave();
      };

      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [
    hoverTooltipsEnabled,
    legalChatbotTourGuide.isActive,
    chatbotDropdownStep,
    chatbotDropdownHover.hoverProps,
    chatbotDropdownHover.tooltipData,
  ]);

  return {
    modelSelectorRef,
    pillToggleRef,
    textareaRef,
    suggestedQuestionsRef,
    legalChatbotTourGuide,
    legalChatbotTourConfig,
    isLegalChatbot,
    modelSelectorHover,
    pillToggleHover,
    textareaHover,
    chatbotDropdownHover,
    suggestedQuestionsHover,
  };
};
